import API_BASE from '../../utils/api';
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import ProfileDropdown from "../../components/ProfileDropdown";

const TeacherSelfieAttendance = () => {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [status, setStatus] = useState(null); // today's attendance status
  const [schoolLocation, setSchoolLocation] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState("");
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [distance, setDistance] = useState(null);

  // Fetch today's status on mount
  useEffect(() => {
    fetchStatus();
    return () => stopCamera();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}/attendance/teacher/selfie-status`
      );
      setStatus(res.data);
      setSchoolLocation(res.data.schoolLocation);
    } catch (err) {
      console.error("Error fetching status:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const now = new Date();
      const res = await axios.get(
        `${API_BASE}/attendance/teacher/selfie-history?month=${now.getMonth() + 1}&year=${now.getFullYear()}`
      );
      setHistory(res.data.records || []);
      setShowHistory(true);
    } catch (err) {
      console.error("Error fetching history:", err);
    }
  };

  // Get current GPS location
  const getLocation = useCallback(() => {
    setLocationError("");
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        setCurrentLocation(loc);

        // Calculate distance if school location is known
        if (schoolLocation) {
          const d = haversineDistance(
            loc.latitude,
            loc.longitude,
            schoolLocation.latitude,
            schoolLocation.longitude
          );
          setDistance(Math.round(d));
        }
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError(
              "Location permission denied. Please enable GPS and allow location access."
            );
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            setLocationError(
              "Location request timed out. Please try again."
            );
            break;
          default:
            setLocationError("An unknown error occurred getting location.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [schoolLocation]);

  // Haversine formula (client-side for UI feedback)
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
      setCapturedImage(null);
    } catch (err) {
      setMessage({
        type: "error",
        text: "Camera access denied. Please allow camera permission.",
      });
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  // Capture selfie
  const captureSelfie = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    const imageData = canvas.toDataURL("image/jpeg", 0.7);
    setCapturedImage(imageData);
    stopCamera();
  };

  // Retake selfie
  const retakeSelfie = () => {
    setCapturedImage(null);
    startCamera();
  };

  // Submit attendance
  const submitAttendance = async () => {
    if (!capturedImage) {
      setMessage({ type: "error", text: "Please capture a selfie first." });
      return;
    }
    if (!currentLocation) {
      setMessage({
        type: "error",
        text: "Location not detected. Please enable GPS and click 'Detect Location'.",
      });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const res = await axios.post(
        `${API_BASE}/attendance/teacher/selfie-attendance`,
        {
          selfieImage: capturedImage,
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
        }
      );
      setMessage({ type: "success", text: res.data.message });
      setCapturedImage(null);
      fetchStatus(); // Refresh status
    } catch (err) {
      const errMsg =
        err.response?.data?.error || "Failed to mark attendance. Try again.";
      setMessage({ type: "error", text: errMsg });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const alreadyMarked = status?.markedToday;
  const isWithinRange =
    distance !== null && schoolLocation
      ? distance <= schoolLocation.radiusInMeters
      : null;

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">
              My Attendance (Selfie)
            </h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6 max-w-2xl mx-auto">
          {/* School Location Not Configured */}
          {!schoolLocation && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-yellow-800">
                School Location Not Set
              </h3>
              <p className="text-yellow-700 mt-2">
                The school location has not been configured yet. Please contact
                your administrator to set the school GPS location.
              </p>
            </div>
          )}

          {/* Already Marked */}
          {alreadyMarked && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="flex items-center">
                <span className="text-4xl mr-4">✅</span>
                <div>
                  <h3 className="text-lg font-semibold text-green-800">
                    Attendance Marked for Today
                  </h3>
                  <p className="text-green-700">
                    Marked at:{" "}
                    {status.teacherLog?.selfieAttendanceAt
                      ? new Date(
                          status.teacherLog.selfieAttendanceAt
                        ).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "N/A"}
                  </p>
                  <p className="text-green-600 text-sm">
                    Distance from school:{" "}
                    {status.teacherLog?.distanceFromSchool ?? "N/A"}m
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mark Attendance Section */}
          {!alreadyMarked && schoolLocation && (
            <div className="space-y-6">
              {/* Step 1: Location */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <span className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">
                    1
                  </span>
                  Verify Your Location
                </h2>
                <p className="text-gray-600 text-sm mb-4">
                  Your location must be within{" "}
                  <strong>{schoolLocation.radiusInMeters}m</strong> of{" "}
                  {schoolLocation.schoolName || "school"} premises.
                </p>

                <button
                  onClick={getLocation}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
                >
                  📍 Detect My Location
                </button>

                {locationError && (
                  <p className="mt-3 text-red-600 text-sm">{locationError}</p>
                )}

                {currentLocation && (
                  <div className="mt-4 p-4 rounded-lg border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">
                          GPS Accuracy: ~{Math.round(currentLocation.accuracy)}m
                        </p>
                        {distance !== null && (
                          <p
                            className={`text-sm font-semibold mt-1 ${
                              isWithinRange
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {isWithinRange
                              ? `✅ Within school premises (${distance}m away)`
                              : `❌ Outside school premises (${distance}m away — max ${schoolLocation.radiusInMeters}m)`}
                          </p>
                        )}
                      </div>
                      <span
                        className={`text-3xl ${
                          isWithinRange ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        {isWithinRange ? "🟢" : "🔴"}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Step 2: Selfie */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <span className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">
                    2
                  </span>
                  Take Selfie
                </h2>
                <p className="text-gray-600 text-sm mb-4">
                  Capture a clear selfie as proof of attendance.
                </p>

                {/* Camera View */}
                {cameraActive && (
                  <div className="relative mb-4">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full rounded-lg border-2 border-indigo-300"
                      style={{ maxHeight: 400 }}
                    />
                    <button
                      onClick={captureSelfie}
                      className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white text-indigo-700 font-semibold px-6 py-3 rounded-full shadow-lg hover:bg-indigo-50 transition border-2 border-indigo-600"
                    >
                      📷 Capture
                    </button>
                  </div>
                )}

                {/* Captured Image Preview */}
                {capturedImage && (
                  <div className="mb-4">
                    <img
                      src={capturedImage}
                      alt="Selfie preview"
                      className="w-full rounded-lg border-2 border-green-300"
                      style={{ maxHeight: 400, objectFit: "cover" }}
                    />
                    <button
                      onClick={retakeSelfie}
                      className="mt-3 text-indigo-600 hover:underline text-sm"
                    >
                      🔄 Retake Selfie
                    </button>
                  </div>
                )}

                {/* Start Camera Button */}
                {!cameraActive && !capturedImage && (
                  <button
                    onClick={startCamera}
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
                  >
                    📷 Open Camera
                  </button>
                )}

                <canvas ref={canvasRef} className="hidden" />
              </div>

              {/* Step 3: Submit */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <span className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm mr-3">
                    3
                  </span>
                  Submit Attendance
                </h2>

                {message && (
                  <div
                    className={`mb-4 p-4 rounded-lg ${
                      message.type === "success"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {message.text}
                  </div>
                )}

                <button
                  onClick={submitAttendance}
                  disabled={
                    submitting ||
                    !capturedImage ||
                    !currentLocation ||
                    !isWithinRange
                  }
                  className={`w-full py-3 rounded-lg text-white font-semibold text-lg transition ${
                    submitting ||
                    !capturedImage ||
                    !currentLocation ||
                    !isWithinRange
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {submitting ? "Submitting..." : "✅ Mark Attendance"}
                </button>

                {(!capturedImage || !currentLocation || !isWithinRange) && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {!currentLocation
                      ? "Please detect your location first (Step 1)"
                      : !isWithinRange
                        ? "You must be within school premises"
                        : "Please capture a selfie (Step 2)"}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* History Toggle */}
          <div className="mt-6">
            <button
              onClick={fetchHistory}
              className="text-indigo-600 hover:underline text-sm font-medium"
            >
              {showHistory
                ? "Refresh Attendance History"
                : "View This Month's Attendance History"}
            </button>
          </div>

          {/* History Table */}
          {showHistory && history.length > 0 && (
            <div className="bg-white rounded-lg shadow mt-4 overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">Attendance History</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Distance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Method
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {history.map((record, idx) => (
                      <tr key={idx}>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {new Date(record.date).toLocaleDateString("en-IN")}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {record.selfieAttendanceAt
                            ? new Date(
                                record.selfieAttendanceAt
                              ).toLocaleTimeString("en-IN", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })
                            : "-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {record.distanceFromSchool != null
                            ? `${record.distanceFromSchool}m`
                            : "-"}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className="px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-700">
                            {record.attendanceMethod || "manual"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {showHistory && history.length === 0 && (
            <div className="bg-white rounded-lg shadow mt-4 p-6 text-center text-gray-500">
              No attendance records found for this month.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherSelfieAttendance;
